import { FeatureFlagKey } from "@redotech/feature-flags-model/keys";
import {
  booleanJsonFormat,
  objectJsonFormat,
  type JsonFormat,
} from "@redotech/json/format";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useRpcData } from "@redotech/react-util/rpc-data";
import { useStore } from "@redotech/react-util/store";
import AlertIcon from "@redotech/redo-icons/alert-triangle_filled.svg";
import BellIcon from "@redotech/redo-icons/bell-03_filled.svg";
import CheckIcon from "@redotech/redo-icons/check-done-01_filled.svg";
import CreditCardIcon from "@redotech/redo-icons/credit-card-check_filled.svg";
import MailIcon from "@redotech/redo-icons/mail-02_filled.svg";
import PackageIcon from "@redotech/redo-icons/package_filled.svg";
import SettingsIcon from "@redotech/redo-icons/settings-02.svg";
import TruckIcon from "@redotech/redo-icons/truck-02_filled.svg";
import UserIcon from "@redotech/redo-icons/user-check-01_filled.svg";
import { MerchantAppTopic } from "@redotech/redo-merchant-app-common/events/merchant-app-event-server";
import { MerchantAppEventServerContext } from "@redotech/redo-merchant-app-common/events/merchant-app-event-server-provider";
import { RedoMerchantNotificationsRpcClientContext } from "@redotech/redo-merchant-app-common/notifications-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import type { RedoMerchantNotificationsRpcClient } from "@redotech/redo-merchant-notifications-rpc/client";
import {
  NotificationKind,
  NotificationSource,
  type NotificationKindType,
  type NotificationSourceType,
} from "@redotech/redo-merchant-notifications/notification-definitions";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import {
  RedoList,
  type RedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { ToggleSwitch } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@redotech/redo-web/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@redotech/redo-web/components/ui/popover";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { formatTimeAgo } from "@redotech/util/date-format";
import { jsonStore, localStore } from "@redotech/web-util/storage";
import {
  memo,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSXElementConstructor,
} from "react";
import { useNavigate } from "react-router-dom";
import * as styles from "./notifications-button.module.css";

const NOTIFICATION_LIMIT = 50;
interface NotificationViewSettings {
  readonly showRead: boolean;
  readonly showUnreadFirst: boolean;
}

const DEFAULT_NOTIFICATION_VIEW_SETTINGS: NotificationViewSettings = {
  showRead: false,
  showUnreadFirst: true,
};

const notificationViewSettingsFormat: JsonFormat<NotificationViewSettings> =
  objectJsonFormat(
    { showRead: booleanJsonFormat, showUnreadFirst: booleanJsonFormat },
    {},
  );

const notificationViewSettingsStore = jsonStore(
  localStore("redo.notification-view-settings"),
  notificationViewSettingsFormat,
);

type NotificationItem = Awaited<
  ReturnType<RedoMerchantNotificationsRpcClient["getCurrentUserNotifications"]>
>["notifications"][number];

const EMPTY_NOTIFICATIONS: NotificationItem[] = [];

export const NotificationsButton = memo(function NotificationsButton() {
  const team = useContext(TeamContext);

  if (team?.featureFlags?.[FeatureFlagKey.MERCHANT_NOTIFICATIONS] !== true) {
    return null;
  }

  return <NotificationsButtonContent />;
});

const NotificationsButtonContent = memo(function NotificationsButtonContent() {
  const client = useRequiredContext(RedoMerchantNotificationsRpcClientContext);
  const eventServer = useContext(MerchantAppEventServerContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [storedSettings, setStoredSettings] = useStore(
    notificationViewSettingsStore,
  );
  const [focusedIndex, setFocusedIndex] = useState<number | undefined>(
    undefined,
  );
  const [listRef, setListRef] = useState<HTMLDivElement | null>(null);
  const viewSettings = storedSettings ?? DEFAULT_NOTIFICATION_VIEW_SETTINGS;
  const { showRead, showUnreadFirst } = viewSettings;
  const unreadOnly = !showRead;

  const updateViewSettings = useHandler(
    (updates: Partial<NotificationViewSettings>) => {
      setStoredSettings((prev) => ({
        ...(prev ?? DEFAULT_NOTIFICATION_VIEW_SETTINGS),
        ...updates,
      }));
    },
  );

  const { value, pending, error, refresh, mutations } = useRpcData(
    client,
    "getCurrentUserNotifications",
    {
      input: {
        limit: NOTIFICATION_LIMIT,
        unreadOnly,
        unreadFirst: showUnreadFirst,
      },
      mutations: ["markNotificationRead"],
    },
  );

  const unreadCount = value?.unreadCount ?? 0;
  const notifications = value?.notifications ?? EMPTY_NOTIFICATIONS;

  useEffect(() => {
    if (!eventServer) {
      return;
    }

    return eventServer.subscribe({
      topic: MerchantAppTopic.USER_NOTIFICATIONS,
      callback: () => refresh(true),
    });
  }, [eventServer, refresh]);

  const handleOpenChange = useHandler((nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setSettingsOpen(false);
    }
    if (nextOpen) {
      refresh();
    }
  });

  const handleNotificationClick = useHandler(
    async (notification: NotificationItem) => {
      if (!notification.readAt) {
        await mutations.markNotificationRead({
          notificationId: notification.notificationId,
        });
      }
      setOpen(false);
      if (!notification.url) {
        return;
      }
      if (notification.url.startsWith("/")) {
        navigate(notification.url);
      } else {
        window.location.href = notification.url;
      }
    },
  );

  const handleMarkVisibleRead = useHandler(async () => {
    await mutations.markNotificationRead({ all: true });
  });

  const notificationItems: RedoListItem<NotificationItem>[] = useMemo(
    () =>
      notifications.map((notification) => {
        const Icon = getNotificationIcon(
          notification.source,
          notification.kind,
        );
        const unread = !notification.readAt;
        const isError = isErrorNotification(notification.kind);

        return {
          id: notification.notificationId,
          type: "custom" as const,
          useWrapper: true,
          selectionVariant: "none",
          value: notification,
          render: (
            <Flex
              align="flex-start"
              className={styles.itemContent}
              gap="md"
              p="md"
              w="full"
            >
              <RedoFeaturedIcon
                color={isError ? "error" : "black"}
                Icon={Icon}
                shape="circle"
                size="sm"
                type="high-contrast"
              />
              <Flex dir="column" gap="none" grow={1} minW="none">
                <Text
                  as="span"
                  fontSize="sm"
                  fontWeight="medium"
                  textColor={unread ? "primary" : "brand-tertiary"}
                >
                  {notification.title}
                </Text>
                <Text as="span" fontSize="xs" textColor="brand-tertiary">
                  {notification.message}
                </Text>
                <Text as="span" fontSize="xxs" textColor="brand-tertiary">
                  {formatTimeAgo(notification.createdAt.toISOString())}
                </Text>
              </Flex>
              {unread && (
                <Flex
                  alignSelf="flex-start"
                  className={styles.unreadDot}
                  radius="full"
                />
              )}
            </Flex>
          ),
        };
      }),
    [notifications],
  );

  return (
    <DropdownMenu onOpenChange={handleOpenChange} open={open}>
      <Flex className={styles.triggerShell}>
        <DropdownMenuTrigger asChild>
          <RedoButton
            aria-label={
              unreadCount > 0
                ? `Open notifications, ${unreadCount} unread`
                : "Open notifications"
            }
            buttonClassName={styles.triggerButton}
            className="dark-mode"
            hierarchy="tertiary"
            IconLeading={BellIcon}
            size="md"
          />
        </DropdownMenuTrigger>
        {unreadCount > 0 && (
          <Flex
            as="span"
            className={styles.triggerBadge}
            position="absolute"
            radius="full"
            right="xs"
            top="xs"
          />
        )}
      </Flex>
      <DropdownMenuContent
        align="end"
        alignOffset={8}
        className={`${styles.content} dark-mode`}
        sideOffset={16}
      >
        <Flex
          align="center"
          className={styles.header}
          justify="space-between"
          p="xl"
        >
          <Text
            as="div"
            fontSize="md"
            fontWeight="semibold"
            textColor="primary"
          >
            Inbox ({unreadCount})
          </Text>
          <Flex align="center" gap="sm">
            <Tooltip arrow darkMode placement="top" title="Mark all as read">
              <Flex as="span">
                <RedoButton
                  aria-label="Mark all notifications read"
                  buttonClassName={styles.actionButton}
                  className="dark-mode"
                  disabled={unreadCount === 0}
                  hierarchy="tertiary"
                  IconLeading={CheckIcon}
                  onClick={() => {
                    void handleMarkVisibleRead();
                  }}
                  size="sm"
                />
              </Flex>
            </Tooltip>
            <Popover
              modal={false}
              onOpenChange={setSettingsOpen}
              open={settingsOpen}
            >
              <PopoverTrigger asChild>
                <RedoButton
                  aria-label="Open notification view settings"
                  buttonClassName={`${styles.actionButton} ${
                    settingsOpen ? styles.actionButtonActive : ""
                  }`}
                  className="dark-mode"
                  hierarchy="tertiary"
                  IconLeading={SettingsIcon}
                  pressed={settingsOpen}
                  size="sm"
                />
              </PopoverTrigger>
              <PopoverContent
                align="end"
                className={`${styles.settingsContent} dark-mode`}
                onOpenAutoFocus={(event) => event.preventDefault()}
                sideOffset={12}
              >
                <Flex dir="column" gap="xs">
                  <Text fontSize="xs" textColor="brand-tertiary">
                    View settings
                  </Text>
                  <Flex className={styles.settingsPanel} dir="column">
                    <NotificationViewToggle
                      label="Show read"
                      setValue={(value) =>
                        updateViewSettings({ showRead: value })
                      }
                      value={showRead}
                    />
                    <NotificationViewToggle
                      label="Show unread first"
                      setValue={(value) =>
                        updateViewSettings({ showUnreadFirst: value })
                      }
                      value={showUnreadFirst}
                    />
                  </Flex>
                </Flex>
              </PopoverContent>
            </Popover>
          </Flex>
        </Flex>

        <Flex className={styles.list} dir="column" ref={setListRef}>
          {error ? (
            <Flex align="center" className={styles.state} justify="center">
              <Text as="div" fontSize="sm" textAlign="center" textColor="error">
                Notifications failed to load.
              </Text>
            </Flex>
          ) : pending && !value ? (
            <Flex align="center" className={styles.state} justify="center">
              <Text
                as="div"
                fontSize="sm"
                textAlign="center"
                textColor="secondary"
              >
                Loading notifications...
              </Text>
            </Flex>
          ) : notifications.length === 0 ? (
            <Flex align="center" className={styles.state} justify="center">
              <Text
                as="div"
                fontSize="sm"
                textAlign="center"
                textColor="secondary"
              >
                {unreadOnly
                  ? "No unread notifications."
                  : "No notifications yet."}
              </Text>
            </Flex>
          ) : (
            <RedoList
              emptyListMessage={
                unreadOnly
                  ? "No unread notifications."
                  : "No notifications yet."
              }
              focusedIndex={focusedIndex}
              items={notificationItems}
              itemSelected={({ item }) => {
                void handleNotificationClick(item.value);
              }}
              refToListenTo={listRef}
              setFocusedIndex={setFocusedIndex}
              size="md"
            />
          )}
        </Flex>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

const NotificationViewToggle = memo(function NotificationViewToggle({
  label,
  value,
  setValue,
}: {
  readonly label: string;
  readonly value: boolean;
  readonly setValue: (value: boolean) => void;
}) {
  return (
    <Flex
      align="center"
      as="label"
      className={styles.settingsRow}
      justify="space-between"
    >
      <Text fontSize="xs" fontWeight="medium" textColor="primary">
        {label}
      </Text>
      <ToggleSwitch
        disabled={false}
        semantic
        setValue={setValue}
        size="xs"
        value={value}
      />
    </Flex>
  );
});

function getNotificationIcon(
  source: NotificationSourceType,
  kind: NotificationKindType,
): JSXElementConstructor<{ className?: string }> {
  if (isErrorNotification(kind)) {
    return AlertIcon;
  }

  if (kind === NotificationKind.BILLING) {
    return CreditCardIcon;
  }

  switch (source) {
    case NotificationSource.ORDER_TRACKING:
      return MailIcon;
    case NotificationSource.OUTBOUND_LABELS:
      return TruckIcon;
    case NotificationSource.INVENTORY:
      return PackageIcon;
    case NotificationSource.SUPPORT:
      return UserIcon;
    case NotificationSource.SYSTEM:
      return SettingsIcon;
    case NotificationSource.RETURNS:
      return PackageIcon;
  }
}

function isErrorNotification(kind: NotificationKindType): boolean {
  switch (kind) {
    case NotificationKind.ORDER_TRACKING_DELAYED:
    case NotificationKind.ORDER_TRACKING_DELIVERY_FAILED:
    case NotificationKind.ORDER_TRACKING_DELIVERY_ERROR:
    case NotificationKind.OUTBOUND_LABELS_FUNDS_LOW:
    case NotificationKind.INVENTORY_LOW_STOCK:
      return true;
    default:
      return false;
  }
}
